import React from "react"
import { Slot } from "radix-ui"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@core/lib/utils"
import { motion } from "motion/react"
import usePerformanceStore from "@core/lib/zustand/stores/general/performance-store"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all perf-no-transitions:transition-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs perf-no-shadows:shadow-none hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs perf-no-shadows:shadow-none hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs perf-no-shadows:shadow-none hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs perf-no-shadows:shadow-none hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

type ButtonVariants = React.ComponentProps<"button"> & VariantProps<typeof buttonVariants> & {
  asChild?: boolean
  animationProps?: Omit<React.ComponentProps<typeof motion.button>, "onDrag">
}

/**
 * Button primitive with CVA variants, optional Slot composition (asChild), and
 * conditional motion/react animation support.
 *
 * When `categories.animations` is false in the performance store, renders a
 * plain `<button>` to avoid motion reconciler overhead.  All CVA variants,
 * `data-slot`, and forwarded props are identical in both branches.
 * `animationProps` is silently ignored in the disabled path — it only carries
 * meaning when a motion element is rendered.  In the motion branch, caller
 * `props` always take final precedence (`{...animationProps, ...props}`) so
 * that `onClick`, `type`, `disabled`, and `aria-*` attrs are never shadowed.
 *
 * The `asChild` path (Slot.Root) is unaffected and never uses motion.
 */
const Button = ({
  className,
  variant,
  size,
  asChild = false,
  animationProps = {},
  ...props
}: ButtonVariants) => {
  const animationsEnabled = usePerformanceStore((s) => s.categories.animations)

  if (asChild) {
    return (
      <Slot.Root
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    )
  }

  if (!animationsEnabled) {
    return (
      <button
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    )
  }

  return (
    <motion.button
      type="button"
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...{...animationProps, ...props} as React.ComponentProps<typeof motion.button>}
    />
  )
}

export { Button, buttonVariants }
